import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { TextInput } from '../../../../ui/input/text_input/text_input';
import { ToggleInput } from '../../../../ui/input/toggle_input/toggle_input';

type SessionTitleProps = {
  sessionTitle: string;
  defaultValue: string;
  setSessionTitle: Dispatch<SetStateAction<string>>;
  disabled?: boolean;
};

export const SessionTitle: React.FC<SessionTitleProps> = ({
  sessionTitle,
  defaultValue,
  setSessionTitle,
  disabled,
}) => {
  const [active, setActive] = useState<boolean>(true);

  useEffect(() => {
    if (active) {
      setSessionTitle(defaultValue);
    }
  }, [active, defaultValue, setSessionTitle]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!active) {
      setSessionTitle(event.target.value);
    }
  };

  const setActiveStatus = () => {
    if (disabled) return;
    setActive(!active);
    if (!active) {
      setSessionTitle(defaultValue);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              mb: 1.5,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'center' },
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
                Generation Title
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Use auto title for consistent generated-question sessions.
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'inline-flex',
                width: 'fit-content',
                alignItems: 'center',
                gap: 1,
                borderRadius: 4,
                border: '1px solid',
                borderColor: active ? 'primary.light' : 'divider',
                bgcolor: active ? 'rgba(16, 185, 129, 0.08)' : 'action.hover',
                px: 1.5,
                py: 0.5,
                fontSize: '0.75rem',
                fontWeight: 700,
                color: active ? 'primary.main' : 'text.secondary',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'inherit' }}>
                {active ? 'Auto title' : 'Manual title'}
              </Typography>
              <ToggleInput
                toggled={active}
                handleToggle={setActiveStatus}
                name="default"
                disabled={!!disabled}
              />
            </Box>
          </Box>

          <TextInput
            name="topic"
            value={sessionTitle || ''}
            type="text"
            onChange={handleInputChange}
            placeholder="Add session title..."
            disabled={active || !!disabled}
          />
          <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary', display: 'block' }}>
            {active
              ? 'Auto generated from the topic and timestamp.'
              : 'Manual title enabled.'}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};
